import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import React, { FC, useEffect, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { CgProfile } from 'react-icons/cg';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import Modal from 'react-modal';

import { useWindowSize } from '../../../hooks';
import { getOneCase } from '../../../services/cases/getCaseById';
import { getUserAvatar } from '../../../services/identity/getUserAvatar';
import { UserProfile } from '../../../services/identity/types/UserProfile';
import { customStylesDashboard } from '../../../Utils/customStyles';
import { displayErrorDetails } from '../../../Utils/displayError';
import styles from './styles.module.scss';

interface ProfileCardModalProps {
  isOpen: boolean;
  toggleModal: (bool: boolean) => void;
  careAdvisorIdentity: UserProfile | null;
  caseId?: number;
}

const desktopModalStyles = {
  ...customStylesDashboard,
  overlay: {
    ...customStylesDashboard.overlay,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  content: {
    ...customStylesDashboard.content,
    borderRadius: '4px',
    boxShadow: '0 8px 10px rgba(0,0,0,0.20), 0 16px 24px rgba(0,0,0,0.14), 0 6px 30px rgba(0,0,0,0.12)',
    padding: '0',
  },
};

const mobileCustomStylesDashboard = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: '95vw',
    height: '78vh',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    zIndex: 10000,
    borderRadius: '4px',
    boxShadow: '0 8px 10px rgba(0,0,0,0.20), 0 16px 24px rgba(0,0,0,0.14), 0 6px 30px rgba(0,0,0,0.12)',
    padding: '0',
  },
};

const CareAdvisorProfileCardModal: FC<ProfileCardModalProps> = ({
  isOpen,
  toggleModal,
  careAdvisorIdentity,
  caseId,
}) => {
  const isMobile = useWindowSize().width < 992;
  const [avatarIcon, setAvatarIcon] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [careAdvisorIdentityFromCase, setCareAdvisorIdentityFromCase] = useState<UserProfile | null>(null);

  const initIdentity = async (): Promise<void> => {
    try {
      setLoadingState(true);
      if (caseId && careAdvisorIdentity?.basic_info === undefined) {
        const thisCase = await getOneCase(caseId);
        if (thisCase) {
          setCareAdvisorIdentityFromCase(thisCase.care_advisor);
        }
      }
      setLoadingState(false);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    initIdentity();
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else if (document.body.classList.contains('modal-open') && !isOpen) {
      document.body.classList.remove('modal-open');
    }
  }, [isOpen]);
  const closeModal = (): void => {
    toggleModal(false);
  };

  const handleAvatarError = (): void => {
    setAvatarIcon(true);
  };

  if (isMobile) {
    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        style={mobileCustomStylesDashboard}
        contentLabel="Modal"
        ariaHideApp={false}
      >
        <div className={styles.profileDashboardContainer}>
          <div className={styles.profileCardCareAdvisor}>
            <button type="button" className={styles.backButton} onClick={closeModal} aria-label="Back">
              <MdKeyboardArrowLeft />
              Back
            </button>
            {loadingState ? (
              <Loading />
            ) : (
              <div className={styles.contentArea}>
                <div className={styles.profileContainer}>
                  <div className={styles.imageContainer}>
                    {!avatarIcon ? (
                      <img
                        alt="Avatar"
                        onError={handleAvatarError}
                        className={`fs-exclude ${styles.careAdvisorImage}`}
                        src={getUserAvatar(careAdvisorIdentity?.id || 0)}
                      />
                    ) : (
                      <CgProfile className="fs-exclude" />
                    )}
                  </div>
                  <div className={styles.textContainer}>
                    <div className={`fs-exclude ${styles.title}`}>
                      {careAdvisorIdentity?.full_name}{' '}
                      {careAdvisorIdentity?.status === 'Deactivated' && '(Deactivated)'}
                    </div>
                  </div>
                </div>
                <hr className={styles.divider} />
                <div className={styles.profileCardBody}>
                  <div className={styles.bio}>
                    <div className={`fs-unmask ${styles.title}`}>About me</div>
                    <div className={`fs-exclude ${styles.content}`}>
                      {careAdvisorIdentity?.care_advisor_profile !== undefined
                        ? careAdvisorIdentity?.care_advisor_profile?.biography
                        : careAdvisorIdentityFromCase?.care_advisor_profile?.biography}
                    </div>
                  </div>
                  <div className={styles.languages}>
                    <div className={`fs-unmask ${styles.title}`}>Languages</div>
                    <div className={styles.content}>
                      {careAdvisorIdentity?.basic_info !== undefined
                        ? careAdvisorIdentity?.basic_info?.languages?.map((language, index, array) => (
                            <span key={language.toString()} className="fs-exclude">
                              {typeof language !== 'string' && language.name}
                              {index < array.length - 1 ? ', ' : ''}
                            </span>
                          ))
                        : careAdvisorIdentityFromCase?.basic_info?.languages?.map((language, index, array) => (
                            <span key={language.toString()} className="fs-mask">
                              {typeof language !== 'string' && language.name}
                              {index < array.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={desktopModalStyles}
      contentLabel="Modal"
      ariaHideApp={false}
    >
      <div className={styles.profileCardCareAdvisor}>
        <div className={styles.titleBar}>
          <div className={styles.titleBarLeft}>
            <span className={styles.purpleChip}>Care Advisor</span>
          </div>
          <button
            type="button"
            className={`fs-unmask ${styles.modalClose}`}
            data-micromodal-close=""
            onClick={closeModal}
            aria-label="Close modal"
          >
            <AiOutlineClose className={styles.icon} />
          </button>
        </div>
        <button type="button" className={styles.backButton} onClick={closeModal} aria-label="Back">
          <MdKeyboardArrowLeft />
          Back
        </button>
        {loadingState ? (
          <Loading />
        ) : (
          <div className={styles.contentArea}>
            <div className={styles.profileContainer}>
              <div className={styles.imageContainer}>
                {!avatarIcon ? (
                  <img
                    alt="Avatar"
                    onError={handleAvatarError}
                    className={`fs-exclude ${styles.careAdvisorImage}`}
                    src={getUserAvatar(careAdvisorIdentity?.id || 0)}
                  />
                ) : (
                  <CgProfile className="fs-exclude" />
                )}
              </div>
              <div className={styles.textContainer}>
                <div className={`fs-exclude ${styles.title}`}>{careAdvisorIdentity?.full_name}</div>
              </div>
            </div>
            <hr className={styles.divider} />
            <div className={styles.profileCardBody}>
              <div className={styles.bio}>
                <div className={`fs-unmask ${styles.title}`}>About me</div>
                <div className={`fs-exclude ${styles.content}`}>
                  {careAdvisorIdentity?.care_advisor_profile !== undefined
                    ? careAdvisorIdentity?.care_advisor_profile?.biography
                    : careAdvisorIdentityFromCase?.care_advisor_profile?.biography}
                </div>
              </div>
              <div className={styles.languages}>
                <div className={`fs-unmask ${styles.title}`}>Languages</div>
                <div className={styles.content}>
                  {careAdvisorIdentity?.basic_info !== undefined
                    ? careAdvisorIdentity?.basic_info?.languages?.map((language, index, array) => (
                        <span key={language.toString()} className="fs-exclude">
                          {typeof language !== 'string' && language.name}
                          {index < array.length - 1 ? ', ' : ''}
                        </span>
                      ))
                    : careAdvisorIdentityFromCase?.basic_info?.languages?.map((language, index, array) => (
                        <span key={language.toString()} className="fs-mask">
                          {typeof language !== 'string' && language.name}
                          {index < array.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

Modal.setAppElement('#root');

export default CareAdvisorProfileCardModal;
